import { notEmpty } from '@festina-life/core'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Tabs } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useCallback, useMemo, useState } from 'react'

import { FPlusScopeFilterValueProvider } from '../../context/FPlusScopeFilterValueProvider'
import { useFetchLanguageServiceScope } from '../../uiModels/api/useFetchLanguageServiceScope'
import { useFilteredScope } from '../../uiModels/fplus/useFilteredScope'
import { FPlusContextList } from './FPlusContextList'
import { FPlusDefaultLibraryList } from './FPlusDefaultLibraryList'
import { FPlusScopeFilter } from './FPlusScopeFilter'
import { FPlusVariableScopeList } from './FPlusVariableScopeList'

type FPlusScopeTabsProps = {
  expressionPath?: string
  tabPanelClassName?: string
}

export const FPlusScopeTabs = ({
  expressionPath,
  tabPanelClassName,
}: FPlusScopeTabsProps) => (
  <FPlusScopeFilterValueProvider>
    <FPlusScopeTabsInner
      expressionPath={expressionPath}
      tabPanelClassName={tabPanelClassName}
    />
  </FPlusScopeFilterValueProvider>
)

const FPlusScopeTabsInner = ({
  expressionPath,
  tabPanelClassName,
}: FPlusScopeTabsProps) => {
  const translate = useTranslate()
  const [tabIndex, setTabIndex] = useState(0)

  const { data: scope, isLoading } = useFetchLanguageServiceScope({
    expressionPath: expressionPath ?? '',
  })

  const filteredScope = useFilteredScope(scope)
  const environmentAvailable = useMemo(
    () => !!filteredScope?.environment?.variables?.length,
    [filteredScope],
  )

  const defaultLibrariesAvailable = useMemo<boolean>(
    () => !!filteredScope?.defaultImportedLibraries?.length,
    [filteredScope],
  )

  const contextAvailable = useMemo(
    () => !!filteredScope?.contexts?.length,
    [filteredScope],
  )
  const FPlusScopeTabItems = useMemo(() => {
    const notFinishedLoading = isLoading || !filteredScope
    const environmentTab =
      notFinishedLoading || environmentAvailable
        ? translate('templateViewer.sidebar.scopePopover.environment')
        : undefined

    const defaultLibraryTab =
      notFinishedLoading || defaultLibrariesAvailable
        ? translate('templateViewer.sidebar.scopePopover.defaultLibraries')
        : undefined

    const contextTab =
      notFinishedLoading || contextAvailable
        ? translate('templateViewer.sidebar.scopePopover.context')
        : undefined

    return [environmentTab, defaultLibraryTab, contextTab].filter(notEmpty)
  }, [
    environmentAvailable,
    defaultLibrariesAvailable,
    contextAvailable,
    translate,
    filteredScope,
    isLoading,
  ])

  const handleChange = useCallback(() => {
    setTabIndex(0)
  }, [setTabIndex])

  return (
    <div className="max-h-full min-w-[35em]">
      <Tabs
        items={FPlusScopeTabItems}
        selectedIndex={tabIndex}
        onChange={setTabIndex}
        tabItemsClassName="px-2 dark:border-gray-500 selected:border-hidden"
        renderTabMenuComponent={<FPlusScopeFilter onChange={handleChange} />}
        panelClassName={cx('max-h-[45vh] h-full', tabPanelClassName)}
      >
        {isLoading || !filteredScope ? (
          <CircularSpinner className="m-auto my-2 size-14" />
        ) : (
          environmentAvailable && (
            <FPlusVariableScopeList
              variables={
                filteredScope.environment?.variables?.filter(notEmpty) ?? []
              }
            >
              <p className="text-center text-lg">
                {filteredScope.environment?.environmentName}
              </p>
            </FPlusVariableScopeList>
          )
        )}
        {isLoading || !filteredScope ? (
          <CircularSpinner className="m-auto my-2 size-14" />
        ) : (
          defaultLibrariesAvailable && (
            <FPlusDefaultLibraryList
              defaultLibraries={
                filteredScope.defaultImportedLibraries
                  ?.filter(notEmpty)
                  // notEmpty is for some reason not enough
                  .filter((x) => x !== null) ?? []
              }
            />
          )
        )}
        {isLoading || !filteredScope ? (
          <CircularSpinner className="m-auto my-2 size-14" />
        ) : (
          contextAvailable && (
            <FPlusContextList
              contexts={
                filteredScope.contexts
                  ?.filter(notEmpty)
                  // notEmpty is for some reason not enough
                  .filter((x) => x !== null) ?? []
              }
            />
          )
        )}
      </Tabs>
    </div>
  )
}
